import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';

interface PostalCodeInput {
  placeholder: string;
  maxlength: number;
}

interface PostalCodeValue {
  zipCode: string;
  state: string | null;
  population: string | null;
}

interface PostalCodeEntry {
  state?: string;
  population?: string;
}

@Component({
  selector: 'qb-postal-code-multi',
  templateUrl: './postal-code-multi.component.html',
  styleUrls: ['./postal-code-multi.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QbPostalCodeMultiComponent),
      multi: true,
    },
  ],
})
export class QbPostalCodeMultiComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string;
  @Input() inputs: PostalCodeInput[];
  @Input() url: string;
  @Input() error: string;

  @ViewChildren('zipInput', { read: ElementRef }) private zipInputs!: QueryList<ElementRef<HTMLElement>>;

  zipCodeDigits: string[] = ['', '', '', '', ''];
  zipCode: string;
  state: string | null;
  population: string | null;
  value: PostalCodeValue;

  private readonly postalCodeUrl = 'https://dxii03pxin5m.cloudfront.net/postal-codes.json';
  private readonly destroy$ = new Subject<void>();

  onTouch = () => { };
  onChange = (_: any) => { };

  constructor(private readonly httpClient: HttpClient) { }

  ngOnInit(): void {
    this.writeValue(this.value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByIndex(index: number): number {
    return index;
  }

  onPostalDigitChange(index: number, digit: string): void {
    this.zipCodeDigits[index] = (digit || '').slice(0, 1);
    const normalizedZipCode = this.syncPostalCodeFromDigits();

    if (normalizedZipCode.length !== 5) {
      if (this.zipCodeDigits[index]) {
        setTimeout(() => this.focusNextInput(index), 0);
      }
      return;
    }

    this.httpClient.get<Record<string, PostalCodeEntry>>(this.url || this.postalCodeUrl)
      .subscribe((response) => {
        const postalCodeData = response[normalizedZipCode];

        this.state = postalCodeData?.state || null;
        this.population = postalCodeData?.population || null;
        this.setValue();
      });
  }

  onPostalDigitKeydown(index: number, event: KeyboardEvent): void {
    if (event.key !== 'Backspace' || this.zipCodeDigits[index]) {
      return;
    }

    if (index === 0) {
      return;
    }

    event.preventDefault();
    this.zipCodeDigits[index - 1] = '';
    this.syncPostalCodeFromDigits();
    setTimeout(() => this.focusInput(index - 1), 0);
  }

  writeValue(value: PostalCodeValue): void {
    if (value && value.zipCode) {
      this.zipCodeDigits = this.toZipCodeDigits(value.zipCode);
    }

    if (value && value.zipCode && !value.state && !value.population) {
      this.zipCode = value.zipCode;
      this.onPostalDigitChange(4, this.zipCodeDigits[4]);
      return;
    }

    if (value && value.zipCode) {
      this.zipCode = value.zipCode;
      this.state = value.state;
      this.population = value.population;
      return;
    }

    this.value = null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  private syncPostalCodeFromDigits(): string {
    const normalizedZipCode = this.zipCodeDigits.join('').trim();

    this.zipCode = normalizedZipCode || null;
    this.state = null;
    this.population = null;
    this.setValue();

    return normalizedZipCode;
  }

  private setValue(): void {
    this.value = {
      zipCode: this.zipCode,
      state: this.state,
      population: this.population,
    };
    this.onTouch();
    this.onChange(this.value);
  }

  private focusInput(index: number): void {
    if (index < 0 || index > 4) {
      return;
    }

    const hostElement = this.zipInputs?.toArray()[index]?.nativeElement;
    const inputElement = hostElement?.querySelector('input') as HTMLInputElement | null;

    if (inputElement) {
      inputElement.focus();
      inputElement.select();
    }
  }

  private focusNextInput(index: number): void {
    this.focusInput(index + 1);
  }

  private toZipCodeDigits(zipCode: string): string[] {
    const digits = (zipCode || '').split('').slice(0, 5);
    while (digits.length < 5) {
      digits.push('');
    }
    return digits;
  }
}